import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { lotSummaryApi } from 'api/inventory';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

const COL_HEADER_SX = {
  fontWeight: 600,
  fontSize: 11,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  py: 1,
};

const getDayColor = (lot) => {
  if (lot.isExpired) return 'error.main';
  if (lot.isExpiringSoon) return 'warning.main';
  return 'text.primary';
};

const LotStatusChip = ({ lot }) => {
  if (lot.isExpired) {
    return (
      <Chip size='small' color='error' label='Expired' sx={{ color: '#fff', fontWeight: 600 }} />
    );
  }
  if (lot.isExpiringSoon) {
    return (
      <Chip
        size='small'
        label='Expiring Soon'
        sx={{ bgcolor: '#FFC107', color: '#212121', fontWeight: 600 }}
      />
    );
  }
  return (
    <Chip size='small' color='success' label='Active' sx={{ color: '#fff', fontWeight: 600 }} />
  );
};
LotStatusChip.propTypes = {
  lot: PropTypes.shape({ isExpired: PropTypes.bool, isExpiringSoon: PropTypes.bool }).isRequired,
};

const ManualAdjustLotSelectorDialog = ({
  open,
  ndc,
  drugName,
  manufacturer,
  facilityOptions,
  initialFacilityId,
  initialInventoryItemId,
  lotsRefreshKey,
  onLotSelect,
  onClose,
}) => {
  const dispatch = useDispatch();

  const [facilityId, setFacilityId] = useState(initialFacilityId ?? '');
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchGenRef = useRef(0);

  const showFacilityPicker = !initialFacilityId && facilityOptions.length > 0;

  // The options arrive in the dashboard row's order; the picker offers them A–Z as they read.
  const sortedFacilityOptions = useMemo(() => {
    const optionLabel = (f) =>
      f.state ? `${f.name ?? ''} (${getStateDisplayName(f.state)})` : (f.name ?? '');
    return [...facilityOptions].sort((left, right) =>
      optionLabel(left).localeCompare(optionLabel(right), undefined, { sensitivity: 'base' })
    );
  }, [facilityOptions]);

  const selectedFacility = useMemo(() => {
    if (initialFacilityId) {
      return { fulfillingPharmacyId: initialFacilityId, inventoryItemId: initialInventoryItemId };
    }
    return facilityOptions.find((f) => f.fulfillingPharmacyId === facilityId) ?? null;
  }, [initialFacilityId, initialInventoryItemId, facilityOptions, facilityId]);

  // "Name (State)", the same form the dropdown and the dashboard header use. Facilities share
  // names across states, so the name on its own does not identify one. Display only — it feeds
  // this dialog's header and the adjustment dialog's, both of which just print it.
  const facilityLabel = useMemo(() => {
    const option = facilityOptions.find(
      (f) => f.fulfillingPharmacyId === (initialFacilityId || facilityId)
    );
    if (!option?.name) return '';
    return option.state ? `${option.name} (${getStateDisplayName(option.state)})` : option.name;
  }, [initialFacilityId, facilityOptions, facilityId]);

  // Reset internal state whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setFacilityId(initialFacilityId ?? '');
      setLots([]);
    }
  }, [open, initialFacilityId]);

  useEffect(() => {
    if (!open || !selectedFacility?.fulfillingPharmacyId || !selectedFacility?.inventoryItemId) {
      setLots([]);
      return;
    }
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    setLoading(true);
    lotSummaryApi({
      inventoryItemId: selectedFacility.inventoryItemId,
      ndc,
      fulfillingPharmacyId: selectedFacility.fulfillingPharmacyId,
      // Depleted lots are listed so stock can be added back onto a lot the system believes is
      // empty. Removing from one stays blocked by the adjustable-qty cap in the adjust dialog.
      filter: { includeDepleted: true, includeExpired: true, includeDepletedExpired: true },
    })
      .then((result) => {
        if (fetchGenRef.current !== gen) return;
        setLots(result?.lots ?? []);
      })
      .catch((error) => {
        if (fetchGenRef.current !== gen) return;
        setLots([]);
        dispatch(handleError({ error }));
      })
      .finally(() => {
        if (fetchGenRef.current === gen) setLoading(false);
      });
  }, [open, selectedFacility, ndc, dispatch, lotsRefreshKey]);

  const handleClose = useCallback(() => {
    setFacilityId(initialFacilityId ?? '');
    setLots([]);
    onClose();
  }, [initialFacilityId, onClose]);

  const handleEditQty = useCallback(
    (lot) => {
      onLotSelect(lot, selectedFacility.fulfillingPharmacyId, facilityLabel);
    },
    [onLotSelect, selectedFacility, facilityLabel]
  );

  const facilityChosen = Boolean(selectedFacility?.fulfillingPharmacyId);

  let body;
  if (showFacilityPicker && !facilityChosen) {
    body = (
      <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 5 }}>
        Select a facility above to view available lots.
      </Typography>
    );
  } else if (loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (lots.length === 0) {
    body = (
      <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 5 }}>
        No lots found for this item.
      </Typography>
    );
  } else {
    body = (
      <Table size='small'>
        <TableHead>
          <TableRow>
            {[
              'LOT NUMBER',
              'BUD',
              'REMAINING QTY',
              'WIP',
              SYSTEM_DEFICIT.toUpperCase(),
              'ADJUSTABLE QTY',
              '',
            ].map((col) => (
              <TableCell key={col} sx={COL_HEADER_SX}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {lots.map((lot) => {
            const adjustableQty = lot.availableQty ?? 0;
            return (
              <TableRow key={lot.inventoryLotId} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {lot.lotNumber}
                </TableCell>
                <TableCell sx={{ color: getDayColor(lot) }}>
                  {lot.expirationDate ? format(new Date(lot.expirationDate), 'MM/dd/yyyy') : '—'}
                </TableCell>
                <TableCell>{lot.remainingQty ?? 0}</TableCell>
                <TableCell>{lot.wipQty ?? 0}</TableCell>
                {/* POM-6094 - this dialog asks for depleted lots, so a lot carrying a system
                    deficit claim shows up here as a plain 0. Adjusting stock onto it is how the
                    claim gets resolved, so the claim has to be visible. */}
                <TableCell
                  sx={{
                    color: lot.hasUnverifiedClaim ? 'warning.dark' : 'text.disabled',
                    fontWeight: lot.hasUnverifiedClaim ? 700 : 400,
                  }}
                >
                  {lot.hasUnverifiedClaim ? lot.unverifiedQty : '—'}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: adjustableQty > 0 ? 'success.main' : 'text.disabled',
                  }}
                >
                  {adjustableQty > 0 ? adjustableQty : 'Depleted'}
                </TableCell>
                <TableCell align='right' sx={{ py: 0.5 }}>
                  {/* Enabled at zero too — a depleted lot can still be adjusted in. */}
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => handleEditQty(lot)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit Qty
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { width: 720, maxWidth: 720 } }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {drugName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Typography variant='body2' color='text.secondary'>
              NDC :
            </Typography>
            <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
              {ndc}
            </Typography>
            {manufacturer && (
              <>
                <Typography variant='body2' color='text.disabled' sx={{ mx: 0.5 }}>
                  |
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Manufacturer :
                </Typography>
                <Typography variant='body2'>{manufacturer}</Typography>
              </>
            )}
            {facilityChosen && facilityLabel && (
              <>
                <Typography variant='body2' color='text.disabled' sx={{ mx: 0.5 }}>
                  |
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Facility :
                </Typography>
                <Typography variant='body2'>{facilityLabel}</Typography>
              </>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} size='small' sx={{ mt: -0.25, ml: 1 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Divider />

      {/* ── Facility picker (global view only) ── */}
      {showFacilityPicker && (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <TextField
            select
            size='small'
            label='Facility'
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            sx={{ width: 320 }}
          >
            {sortedFacilityOptions.map((f) => (
              <MenuItem
                key={f.fulfillingPharmacyId}
                value={f.fulfillingPharmacyId}
                disabled={f.isLocked}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                  {f.isLocked && <LockIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />}
                  {/* Same "Name (State)" form the dashboard header and facility switcher use —
                      facilities share names across states, so the name alone is ambiguous. */}
                  <span>{f.state ? `${f.name} (${getStateDisplayName(f.state)})` : f.name}</span>
                  {f.isLocked && (
                    <Typography variant='caption' color='text.disabled' sx={{ ml: 'auto' }}>
                      Locked for count
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {/* ── Lots table / states ── */}
      <Box sx={{ px: 2.5, py: 2, minHeight: 200 }}>{body}</Box>
    </Dialog>
  );
};

ManualAdjustLotSelectorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  ndc: PropTypes.string,
  drugName: PropTypes.string,
  manufacturer: PropTypes.string,
  facilityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      fulfillingPharmacyId: PropTypes.string,
      inventoryItemId: PropTypes.string,
      name: PropTypes.string,
      state: PropTypes.string,
      isLocked: PropTypes.bool,
    })
  ),
  initialFacilityId: PropTypes.string,
  initialInventoryItemId: PropTypes.string,
  lotsRefreshKey: PropTypes.number,
  onLotSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
ManualAdjustLotSelectorDialog.defaultProps = {
  ndc: '',
  drugName: '',
  manufacturer: '',
  facilityOptions: [],
  initialFacilityId: undefined,
  initialInventoryItemId: undefined,
  lotsRefreshKey: 0,
};

export default ManualAdjustLotSelectorDialog;
